import type { WardCreateInput } from "@handoverpro/shared";

import {
  archiveWard as sdkArchiveWard,
  createWard as sdkCreateWard,
  listWards as sdkListWards,
  updateWard as sdkUpdateWard,
  type AdminWardDto,
} from "~/lib/api";

/**
 * Network layer for the super-admin ward management screens (V2-E1.2, HAN-86).
 * Wraps the generated SDK calls (HAN-83's `/admin/wards`) in discriminated-union
 * results so the screen stays free of HTTP-status details — mirrors the shape of
 * `~/lib/patients`. The SDK functions are aliased to avoid colliding with the
 * wrappers exported here.
 */

export type FetchResult<T> = { status: "ok"; data: T } | { status: "error"; message: string };

export type SaveResult =
  | { status: "ok"; ward: AdminWardDto }
  | { status: "error"; message: string };

const GENERIC_SAVE_ERROR = "Something went wrong saving the ward. Please try again.";

/** Every ward, archived ones included — the super-admin view is the full history. */
export async function fetchWards(): Promise<FetchResult<AdminWardDto[]>> {
  const result = await sdkListWards();
  if (result.error || !result.data) {
    return { status: "error", message: `Couldn't load the wards (${result.response.status}).` };
  }
  return { status: "ok", data: result.data };
}

function messageForSaveStatus(status: number): string {
  switch (status) {
    case 400:
      return "Check the ward name and short code, then try again.";
    case 403:
      return "Only a super-admin can manage wards.";
    case 404:
      return "That ward no longer exists.";
    case 409:
      return "A ward with that short code already exists.";
    default:
      return GENERIC_SAVE_ERROR;
  }
}

export async function createWard(input: WardCreateInput): Promise<SaveResult> {
  const result = await sdkCreateWard({ body: input });
  if (result.error || !result.data) {
    return { status: "error", message: messageForSaveStatus(result.response.status) };
  }
  return { status: "ok", ward: result.data };
}

/**
 * Edit a ward. The form always carries both fields, so both are sent (a PATCH
 * with both values trivially satisfies the API's "at least one field" rule).
 */
export async function updateWard(wardId: string, input: WardCreateInput): Promise<SaveResult> {
  const result = await sdkUpdateWard({ path: { wardId }, body: input });
  if (result.error || !result.data) {
    return { status: "error", message: messageForSaveStatus(result.response.status) };
  }
  return { status: "ok", ward: result.data };
}

/**
 * Soft-archive a ward. A 409 means it's already archived (a stale list) — mapped
 * to a message rather than treated as a hard failure.
 */
export async function archiveWard(wardId: string): Promise<SaveResult> {
  const result = await sdkArchiveWard({ path: { wardId } });
  if (result.error || !result.data) {
    const message =
      result.response.status === 409
        ? "That ward is already archived."
        : messageForSaveStatus(result.response.status);
    return { status: "error", message };
  }
  return { status: "ok", ward: result.data };
}
